import { Injectable, inject, signal } from '@angular/core';
import { DebugColor, BoxDetection } from '@models';
import { CanvasService, HeatMapMetricsService, TaskService } from '@services';
import '@tensorflow/tfjs-backend-webgl';

@Injectable({
  providedIn: 'root',
})
export class DebugRenderService {
  private canvasService = inject(CanvasService);
  private taskService = inject(TaskService);
  private heatMapMetricsService = inject(HeatMapMetricsService);

  showInfo = signal(true);
  showCoordinates = signal(false);
  coloredObjects = signal(true);
  workers: string[] = [];
  workerFilter: string[] = [];
  objectColors: { [key: string]: string } = {};
  debugBoxes = new Map<string, Map<string, BoxDetection[]>>();
  debugRegressions = signal<
    { workerId: string; regressions: { name: string; confidence: number }[] }[]
  >([]);

  updateDebugBoxes(worker: string, boxes: Map<string, BoxDetection[]>): void {
    this.debugBoxes.set(worker, boxes);
  }

  updateDebugRegressions(
    worker: string,
    latestRegressions: Map<string, number>,
  ): void {
    let currentRegressions = this.debugRegressions();

    const regressions = Array.from(latestRegressions).map(
      ([name, confidence]) => ({
        name,
        confidence,
      }),
    );

    currentRegressions = currentRegressions.filter(
      (entry) => entry.workerId !== worker,
    );
    currentRegressions.push({ workerId: worker, regressions });
    this.debugRegressions.set(currentRegressions);
  }

  createObjectColors(workerId: string, object: string[]) {
    const currentIndex = Object.keys(this.objectColors).length;
    object.forEach((obj, index) => {
      this.objectColors[workerId + obj] =
        Object.values(DebugColor)[(currentIndex + index) % 16];
    });
  }

  async debugRender(): Promise<void> {
    const canvas: HTMLCanvasElement = document.getElementById(
      'debugCanvas',
    ) as HTMLCanvasElement;
    const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;

    await new Promise<void>((resolve) => {
      ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
      resolve();
    });

    this.heatMapMetricsService.visualizeHeatMap(ctx);
    this.debugBoxes.forEach((boxes, worker) => {
      if (!this.workerFilter.includes(worker)) {
        let index = 0;
        boxes.forEach((entry, name) => {
          if (entry !== null) {
            entry.forEach((box) => {
              const filteredObjects = this.canvasService.objectFilter;

              const positiveFiltered = filteredObjects.filter(
                (obj) => obj[0] !== '-',
              );

              const negativeFiltered = filteredObjects
                .filter((obj) => obj[0] === '-')
                .map((obj) => obj.slice(1));

              if (
                filteredObjects.length === 0 ||
                ((positiveFiltered.length === 0 ||
                  positiveFiltered.some((key) => name.includes(key))) &&
                  filteredObjects.some((key) =>
                    key.includes(name)
                      ? !negativeFiltered.some((key) => name === key)
                      : true,
                  ))
              ) {
                const fontSize = 14;
                ctx.font = fontSize + 'px sans-serif';
                ctx.textBaseline = 'bottom';
                const strokeColor = '#FFFFFF';
                const fillColor = Object.entries(DebugColor).map(
                  ([key, value]) => value,
                );
                const textColor = '#FFFFFF';
                const percentage = +(box.confidence * 100).toFixed(1);

                const x = box.min.x;
                const y = box.min.y;
                const width = box.max.x - box.min.x;
                const height = box.max.y - box.min.y;

                if (name && name.includes('')) {
                  ctx.globalAlpha = 0.3;
                  ctx.fillStyle = this.coloredObjects()
                    ? this.objectColors[worker + name]
                    : fillColor[this.workers.indexOf(worker)];

                  ctx.fillRect(x, y, width, height);

                  ctx.strokeStyle = strokeColor;
                  ctx.lineWidth = 1;
                  ctx.strokeRect(x, y, width, height);

                  ctx.globalAlpha = 1;
                  ctx.fillStyle = textColor;

                  const textObject = name + ' (' + percentage.toFixed(0) + '%)';

                  const textCoordinates =
                    'min x➜' +
                    box.min.x.toFixed(0) +
                    ' y➜' +
                    box.min.y.toFixed(0) +
                    '  |  max x➜' +
                    box.max.x.toFixed(0) +
                    ' y➜' +
                    box.max.y.toFixed(0);

                  if (this.showInfo()) {
                    ctx.fillText(
                      textObject,
                      x + width / 2 - ctx.measureText(textObject).width / 2,
                      y - (this.showCoordinates() ? 16 : 0),
                    );
                  }

                  if (this.showCoordinates()) {
                    ctx.fillText(
                      textCoordinates,
                      x +
                        width / 2 -
                        ctx.measureText(textCoordinates).width / 2,
                      y,
                    );
                  }
                }
              }
            });
          }
          index++;
        });
      }
    });
  }
}
